import { useParams, Link } from 'react-router-dom';
import { useWagerDetail, useWagerPosts, useCreatePrompt } from '../hooks/use-api';
import { useAuth } from '../auth/auth-context';
import { Spinner } from '../components/shared/Spinner';
import { EmptyState } from '../components/shared/EmptyState';
import { LoadMoreButton } from '../components/shared/LoadMoreButton';
import { InfoBlock } from '../components/shared/InfoBlock';
import { HexLocationLink } from '../components/shared/HexLocationLink';
import { formatRelativeTime } from '../hooks/use-relative-time';
import styles from './WagerDetailPage.module.css';

type StatusVariant = 'offer' | 'conflict' | 'success' | 'dim';

const statusVariantMap: Readonly<Record<string, StatusVariant>> = {
  open: 'offer',
  matched: 'conflict',
  challenged: 'conflict',
  evidence_submitted: 'conflict',
  awaiting_claim: 'success',
  resolved: 'success',
  voided: 'dim',
};

const statusDisplayMap: Readonly<Record<string, string>> = {
  open: 'Open',
  matched: 'Matched',
  challenged: 'Challenged',
  evidence_submitted: 'Evidence Submitted',
  awaiting_claim: 'Resolved — Awaiting Claim',
  resolved: 'Resolved',
  voided: 'Voided',
};

const getStatusVariant = (status: string): StatusVariant =>
  statusVariantMap[status] ?? 'dim';

type PromptType = 'why_this_side' | 'what_evidence' | 'challenge' | 'what_changed';

const PROMPT_BUTTONS: ReadonlyArray<{ readonly label: string; readonly type: PromptType }> = [
  { label: 'Why this side?', type: 'why_this_side' },
  { label: 'What evidence?', type: 'what_evidence' },
  { label: 'Challenge', type: 'challenge' },
  { label: 'What changed?', type: 'what_changed' },
];

const WagerDetailPage = (): React.ReactElement => {
  const { wagerId } = useParams();
  const safeId = wagerId ?? '';

  const detailQuery = useWagerDetail(safeId);
  const { items: posts, isLoading: postsLoading, hasMore: postsHasMore, loadMore: postsLoadMore, isFetchingNextPage: postsFetchingNext } = useWagerPosts(safeId);
  const createPrompt = useCreatePrompt();

  const { isAuthenticated } = useAuth();

  if (detailQuery.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner />
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className={styles.page}>
        <EmptyState message="Wager not found" />
      </div>
    );
  }

  const wager = detailQuery.data;
  const variant = getStatusVariant(wager.status);
  const forPositions = wager.positions.filter((p: Record<string, unknown>) => p.side === 'for');
  const againstPositions = wager.positions.filter((p: Record<string, unknown>) => p.side === 'against');
  const hasOpposingSide = againstPositions.length > 0;

  const handlePrompt = (promptType: PromptType): void => {
    createPrompt.mutate({ wagerId: safeId, promptType });
  };

  return (
    <div className={styles.page}>
      <button data-testid="back" className={styles.backButton} onClick={() => { window.history.back(); }} type="button">
        &larr; Back
      </button>

      <header className={styles.header}>
        <h1 className={styles.statement}>&ldquo;{wager.statement}&rdquo;</h1>
        <div className={styles.headerMeta}>
          <span className={`${styles.statusBadge} ${styles[variant]}`}>{statusDisplayMap[wager.status] ?? wager.status}</span>
          <span className={styles.category}>{wager.category}</span>
        </div>
      </header>

      <div className={styles.positions}>
        {/* FOR side — creator + allies */}
        <div className={styles.sideColumn}>
          <div className={styles.sideHeader}>FOR</div>
          <div className={styles.positionCard}>
            <div><Link to={`/entity/${wager.creatorId}`} className={styles.entityLink}>{wager.creatorName}</Link></div>
            <div className={styles.positionSide}>Creator</div>
            <div className={`${styles.positionStake} ${styles.stakeCreator}`}>{wager.stake} MC</div>
          </div>
          {forPositions.map((p: Record<string, unknown>) => (
            <div key={p.entityId as string} className={styles.positionCard}>
              <div><Link to={`/entity/${p.entityId as string}`} className={styles.entityLink}>{(p.entityName as string) ?? (p.entityId as string)}</Link></div>
              <div className={styles.positionSide}>Ally</div>
              <div className={`${styles.positionStake} ${styles.stakeCreator}`}>{p.stake as number} MC</div>
            </div>
          ))}
        </div>

        {/* VS divider */}
        <div className={styles.vsDivider}>{hasOpposingSide ? 'VS' : 'AWAITING'}</div>

        {/* AGAINST side */}
        <div className={styles.sideColumn}>
          <div className={styles.sideHeader}>AGAINST</div>
          {againstPositions.length > 0 ? (
            againstPositions.map((p: Record<string, unknown>) => (
              <div key={p.entityId as string} className={styles.positionCard}>
                <div><Link to={`/entity/${p.entityId as string}`} className={styles.entityLink}>{(p.entityName as string) ?? (p.entityId as string)}</Link></div>
                <div className={styles.positionSide}>{p.side as string}</div>
                <div className={`${styles.positionStake} ${styles.stakeOpponent}`}>{p.stake as number} MC</div>
              </div>
            ))
          ) : (
            <div className={styles.positionCardDashed}>
              <span className={styles.awaiting}>No challenger yet</span>
            </div>
          )}
        </div>
      </div>

      <InfoBlock title="How This Resolves">
        <div className={styles.resolutionCriteria}>
          {wager.resolutionCriteria.measurableOutcome ? (
            <div className={styles.criteriaField}>
              <div className={styles.criteriaLabel}>Outcome measured</div>
              <div className={styles.criteriaValue}>{wager.resolutionCriteria.measurableOutcome}</div>
            </div>
          ) : null}
          {wager.resolutionCriteria.successCondition ? (
            <div className={styles.criteriaField}>
              <div className={styles.criteriaLabel}>Success means</div>
              <div className={styles.criteriaValue}>{wager.resolutionCriteria.successCondition}</div>
            </div>
          ) : null}
          {wager.resolutionCriteria.resolutionSource ? (
            <div className={styles.criteriaField}>
              <div className={styles.criteriaLabel}>Evidence from</div>
              <div className={styles.criteriaValue}>{wager.resolutionCriteria.resolutionSource}</div>
            </div>
          ) : null}
          {wager.resolutionCriteria.hexId ? (
            <div className={styles.criteriaField}>
              <div className={styles.criteriaLabel}>Location</div>
              <div className={styles.criteriaValue}>
                <HexLocationLink hexId={wager.resolutionCriteria.hexId} />
              </div>
            </div>
          ) : null}
          {wager.resolutionCriteria.evidenceType ? (
            <div className={styles.criteriaRow}>
              <span className={styles.criteriaLabel}>Evidence type</span>
              <span className={styles.criteriaBadge}>{wager.resolutionCriteria.evidenceType}</span>
            </div>
          ) : null}
        </div>
      </InfoBlock>

      <div className={styles.termsCard}>
        <div className={styles.termsRow}>
          <span className={styles.termsLabel}>Created</span>
          <span>{formatRelativeTime(wager.createdAt)}</span>
        </div>
        {wager.resolutionDate ?? wager.resolutionCriteria.resolutionDate ? (
          <div className={styles.termsRow}>
            <span className={styles.termsLabel}>Resolves</span>
            <span>{formatRelativeTime((wager.resolutionDate ?? wager.resolutionCriteria.resolutionDate) as string)}</span>
          </div>
        ) : null}
        {wager.expiresAt ? (
          <div className={styles.termsRow}>
            <span className={styles.termsLabel}>Expires</span>
            <span>{formatRelativeTime(wager.expiresAt)}</span>
          </div>
        ) : null}
      </div>

      {wager.resolution != null && (
        <InfoBlock title="Resolution">
          <div className={styles.resolutionPanel}>
            <div className={styles.criteriaField}>
              <div className={styles.criteriaLabel}>Winner</div>
              <div className={styles.criteriaValue}>
                <Link to={`/entity/${wager.resolution.winnerId}`} className={styles.entityLink}>
                  {wager.resolution.winnerName}
                </Link>
              </div>
            </div>
            <div className={styles.criteriaField}>
              <div className={styles.criteriaLabel}>Evidence</div>
              <div className={styles.criteriaValue}>{wager.resolution.evidence}</div>
            </div>
            <div className={styles.criteriaRow}>
              <span className={styles.criteriaLabel}>Outcome</span>
              <span className={styles.criteriaValue}>
                {wager.resolution.outcome === 'void' ? 'Stakes returned' : wager.resolution.outcome}
              </span>
            </div>
            <div className={styles.criteriaRow}>
              <span className={styles.criteriaLabel}>Payout</span>
              <span className={styles.criteriaValue}>{wager.resolution.payout} MC</span>
            </div>
          </div>
        </InfoBlock>
      )}

      {wager.topicId !== undefined && (
        <div className={styles.topicLink}>
          <Link to={`/forum/${wager.topicId}`} className={styles.discussionLink}>
            View Discussion →
          </Link>
        </div>
      )}

      <InfoBlock title="Discussion">
        {postsLoading ? (
          <Spinner />
        ) : posts.length === 0 ? (
          <EmptyState message="No discussion yet" />
        ) : (
          <div className={styles.postList}>
            {posts.map((post, idx) => (
              <div key={idx} className={styles.post}>
                <div className={styles.postHeader}>
                  <Link to={`/entity/${post.entityId}`} className={styles.entityLink}>{post.entityName}</Link>
                  <span className={styles.postTime}>{formatRelativeTime(post.createdAt)}</span>
                </div>
                <div className={styles.postContent}>{post.content}</div>
              </div>
            ))}
            <LoadMoreButton onClick={postsLoadMore} isLoading={postsFetchingNext} hasMore={postsHasMore} />
          </div>
        )}
      </InfoBlock>

      {isAuthenticated && (
        <div className={styles.promptButtons}>
          {PROMPT_BUTTONS.map((btn) => (
            <button
              key={btn.type}
              type="button"
              data-testid={`wager-prompt-${btn.type}`}
              className={styles.promptButton}
              onClick={() => { handlePrompt(btn.type); }}
              disabled={createPrompt.isPending}
            >
              {btn.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export { WagerDetailPage };
